import { OfferStatus } from "../types";

export interface VersionInfo {
  id: string;
  status: OfferStatus;
  /**
   * ISO date string (or `Date`) — backend `Date` is serialized as string over JSON.
   * Use `new Date(publishedAt)` to convert.
   */
  publishedAt: Date | string | null;
  createdAt: Date | string;
  updatedAt: Date | string;
  title: string;
}

/**
 * Calculate version number for an offer based on its position in published versions
 * @param versions - All versions of an offer (including main offer and drafts)
 * @param offerId - ID of the offer to calculate version number for
 * @returns Version number (1-based) or null if not a published version
 */
export function calculateVersionNumber(
  versions: VersionInfo[],
  offerId: string
): number | null {
  // Filter only published versions and sort by publishedAt ascending
  const publishedVersions = versions
    .filter((v) => v.status === OfferStatus.PUBLISHED && v.publishedAt)
    .sort(
      (a, b) =>
        new Date(a.publishedAt!).getTime() - new Date(b.publishedAt!).getTime()
    );

  // Find index of current offer in published versions
  const index = publishedVersions.findIndex((v) => v.id === offerId);

  // Return 1-based version number, or null if not found
  return index >= 0 ? index + 1 : null;
}

/**
 * Format published date for display
 * @param date - Date to format
 * @returns Formatted date string in Polish format (DD.MM.YYYY HH:mm)
 */
export function formatPublishedDate(date: Date | string | null): string | null {
  if (!date) return null;

  const dateObj = typeof date === "string" ? new Date(date) : date;

  // Format as DD.MM.YYYY HH:mm
  const day = String(dateObj.getDate()).padStart(2, "0");
  const month = String(dateObj.getMonth() + 1).padStart(2, "0");
  const year = dateObj.getFullYear();
  const hours = String(dateObj.getHours()).padStart(2, "0");
  const minutes = String(dateObj.getMinutes()).padStart(2, "0");

  return `${day}.${month}.${year} ${hours}:${minutes}`;
}

/**
 * Get version display string for an offer
 * @param versions - All versions of the offer
 * @param offerId - ID of the current offer
 * @param publishedAt - Published date of the current offer
 * @returns Formatted version string like "Wersja 3 (28.10.2025 09:00)" or null
 */
export function getVersionDisplayString(
  versions: VersionInfo[],
  offerId: string,
  publishedAt: Date | string | null
): string | null {
  const versionNumber = calculateVersionNumber(versions, offerId);

  if (versionNumber === null) {
    return null;
  }

  const formattedDate = formatPublishedDate(publishedAt);

  if (formattedDate) {
    return `Wersja ${versionNumber} (opublikowana ${formattedDate})`;
  }

  return `Wersja ${versionNumber}`;
}
